import React, { useState, useRef, useEffect } from "react";

/**
 * EQUAL-AREA LOGO SIZING — the single sizing authority for marketing grid
 * cards. No width caps or aspect penalties remain; this algorithm is the
 * only thing that governs logo dimensions.
 *
 * Calibrated target optical area: A = 7300 px² (measured from the approved
 * Yahoo card: 162.25 × 44.99 ≈ 7300).
 *
 * Per logo, at render time:
 *   r = naturalWidth / naturalHeight
 *   h = sqrt(A / r);  w = h × r            → optical area w × h = A
 *   Clamps: h_min 24px; h_max 56px (the logo area's height); w_max 62% of the
 *           card (logo-area) width. If a MAX clamp binds, scale w and h DOWN
 *           proportionally so the bound is respected (aspect preserved).
 *   Multiply by `scale` (the product's logo_display_scale), clamped 0.6–1.5,
 *   then re-apply the same MAX clamps.
 *   h_min is a legibility floor: if h would fall below 24px after the MAX
 *   clamps, scale up to 24px (the card's overflow-hidden is the safety
 *   backstop and should not visibly engage within the card width).
 *   Render the image at exactly w × h, centred in the logo area.
 *   object-fit: contain + the card's overflow-hidden remain as a safety
 *   backstop only — under this algorithm they do not visibly engage.
 *
 * The card (logo-area) width is measured live via a ref + ResizeObserver so
 * w_max (62% of card width) is exact at every responsive breakpoint.
 *
 * Colour-treatment strategies (never sizing):
 *   1. MASK           — CSS alpha mask for transparent logos (grey → black on hover).
 *   2. INVERT         — invert+grayscale+multiply for black-background PNGs.
 *   3. DARK-BG-COLOUR — colour logos on dark backgrounds (multiply blend).
 *   4. OPAQUE         — full opaque image (dimmed grayscale → colour on hover).
 *   5. BLEND (default)— grayscale+multiply (grey → colour on hover).
 *
 * Text fallback for products with no logo (or load error) is unchanged.
 */
const AREA = 7300;
const H_MIN = 24;
const H_MAX = 56;
const W_MAX_RATIO = 0.62;

function computeSize(r, cardWidth, scale) {
  let h = Math.sqrt(AREA / r);
  let w = h * r;
  const wMax = cardWidth * W_MAX_RATIO;

  // Scale DOWN proportionally for any binding MAX clamp (aspect preserved).
  const fDown = Math.min(1, wMax / w, H_MAX / h);
  w *= fDown;
  h *= fDown;

  // Product logo_display_scale, clamped 0.6–1.5.
  const s = Math.max(0.6, Math.min(1.5, Number(scale) || 1));
  w *= s;
  h *= s;

  // Re-apply MAX clamps after the scale multiplier.
  const fDown2 = Math.min(1, wMax / w, H_MAX / h);
  w *= fDown2;
  h *= fDown2;

  // h_min legibility floor: never render shorter than 24px.
  if (h < H_MIN) {
    const up = H_MIN / h;
    w *= up;
    h *= up;
  }

  return { w: Math.round(w), h: Math.round(h) };
}

export default function OutletLogo({ logo, name, scale, maskLogo, invertLogo, colorLogo, darkBgColorLogo, opaqueLogo }) {
  const [logoError, setLogoError] = useState(false);
  const [natural, setNatural] = useState(null);
  const [cardWidth, setCardWidth] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setCardWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleLoad = (e) => {
    const w = e.currentTarget.naturalWidth;
    const h = e.currentTarget.naturalHeight;
    if (!w || !h) { setLogoError(true); return; }
    setNatural({ w, h });
  };

  const rootClass = "relative flex items-center justify-center w-full h-full";

  // Text fallback (no logo / load error) — unchanged.
  if (!logo || logoError) {
    return (
      <span ref={containerRef} className={rootClass}>
        <span className="font-heading text-xl font-bold text-bone">{name}</span>
      </span>
    );
  }

  const size = natural && cardWidth > 0
    ? computeSize(natural.w / natural.h, cardWidth, scale)
    : null;

  // Exact render dimensions once known; pre-load safety style otherwise.
  const imgStyle = size
    ? { width: `${size.w}px`, height: `${size.h}px` }
    : { maxWidth: "100%", maxHeight: "100%" };
  const fitClass = "object-contain";

  const useMask = !!maskLogo;

  if (useMask) {
    const maskBox = size
      ? { width: `${size.w}px`, height: `${size.h}px` }
      : { width: "100%", height: "100%" };
    return (
      <span
        ref={containerRef}
        className={rootClass}
        title={name}
        role="img"
        aria-label={name}
      >
        {/* Invisible image used only for load/error + natural-size detection. */}
        <img
          src={logo}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="absolute opacity-0 pointer-events-none select-none"
          onError={() => setLogoError(true)}
          onLoad={handleLoad}
        />
        <span
          className="bg-[#6c6c74] group-hover/card:bg-[#1c1c20] transition-colors duration-300"
          style={{
            ...maskBox,
            WebkitMaskImage: `url(${logo})`,
            WebkitMaskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            WebkitMaskSize: "contain",
            maskImage: `url(${logo})`,
            maskRepeat: "no-repeat",
            maskPosition: "center",
            maskSize: "contain",
          }}
          aria-hidden="true"
        />
      </span>
    );
  }

  let strategyClass = "grayscale opacity-70 mix-blend-multiply group-hover/card:grayscale-0 group-hover/card:opacity-100 transition-all duration-300";
  if (invertLogo) {
    strategyClass = "grayscale invert opacity-80 mix-blend-multiply group-hover/card:opacity-100 transition-opacity duration-300";
  } else if (darkBgColorLogo) {
    strategyClass = "logo-dark-color";
  } else if (opaqueLogo) {
    strategyClass = "opacity-60 grayscale group-hover/card:opacity-100 group-hover/card:grayscale-0 transition-all duration-300";
  }

  return (
    <span ref={containerRef} className={rootClass}>
      <img
        src={logo}
        alt={name}
        loading="lazy"
        decoding="async"
        className={`${fitClass} ${strategyClass}`}
        style={imgStyle}
        onError={() => setLogoError(true)}
        onLoad={handleLoad}
      />
    </span>
  );
}